import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  API_URL,
  createDirectoryCheckout,
  fetchDirectoryInventory,
  fetchDirectoryPackages,
} from '../api';
import paymentGateway from '../services/paymentGateway';
import { useToast } from '../contexts/ToastContext';
import './DirectoryCheckoutPage.css';

// A campaign is always booked against a listing that already exists, so the
// advertiser picks the listing here rather than describing it again.
const sourceKeyOf = (listing) =>
  listing ? listing.sourceKey || `${listing.source || 'advertisement'}:${listing.id}` : '';

const money = (amount, currency = 'USD') =>
  `${currency === 'USD' ? '$' : ''}${Number(amount || 0).toLocaleString()}`;

const todayIso = () => new Date().toISOString().slice(0, 10);

// One key per checkout attempt, reused across retries so a network failure that
// actually reached the server cannot bill the advertiser twice.
const newIdempotencyKey = () =>
  `directory-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export default function DirectoryCheckoutPage() {
  const navigate = useNavigate();
  const { showSuccess } = useToast();
  const [searchParams] = useSearchParams();
  const cardContainerRef = useRef(null);

  const [packages, setPackages] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [packageId, setPackageId] = useState('');
  const [sourceKey, setSourceKey] = useState(searchParams.get('source') || '');
  const [listingMode, setListingMode] = useState('existing');
  const [newListing, setNewListing] = useState({
    title: '',
    description: '',
    website_url: '',
    phone: '',
    directory_category_id: '',
    directory_location_id: '',
    keywords: '',
  });
  const [startsAt, setStartsAt] = useState(todayIso());
  const [autoRenew, setAutoRenew] = useState(false);
  const [form, setForm] = useState({ advertiser_name: '', advertiser_email: '', advertiser_phone: '' });

  const [paymentCard, setPaymentCard] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const idempotencyKey = useRef(newIdempotencyKey());

  // Load packages, live availability, and the listings eligible for promotion.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [packageBody, inventoryBody, feedResponse] = await Promise.all([
          fetchDirectoryPackages(),
          fetchDirectoryInventory(),
          fetch(`${API_URL}/ads/directory`, {
            cache: 'no-store',
            headers: { Accept: 'application/json' },
          }),
        ]);
        if (cancelled) return;

        const feed = feedResponse.ok ? await feedResponse.json() : {};
        const available = Array.isArray(packageBody.data) ? packageBody.data : [];
        setPackages(available);
        setInventory(Array.isArray(inventoryBody.data) ? inventoryBody.data : []);
        setListings(Array.isArray(feed.listings) ? feed.listings : []);
        setCategories(Array.isArray(feed.categories) ? feed.categories : []);
        setLocations(Array.isArray(feed.locations) ? feed.locations : []);
        if (!Array.isArray(feed.listings) || feed.listings.length === 0) setListingMode('new');

        const requested = searchParams.get('package');
        const preselected =
          available.find((item) => item.slug === requested || String(item.id) === requested) ||
          available[0];
        if (preselected) setPackageId(String(preselected.id));
      } catch (error) {
        if (!cancelled) setLoadError(error.message || 'We could not load the advertising packages.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const selectedPackage = useMemo(
    () => packages.find((item) => String(item.id) === packageId) || null,
    [packages, packageId]
  );

  const availability = useMemo(() => {
    if (!selectedPackage?.placement?.key) return null;
    return inventory.find((item) => item.key === selectedPackage.placement.key) || null;
  }, [inventory, selectedPackage]);

  const soldOut = availability?.available === 0;

  // Mount the secure card field for whichever gateway the backend has live.
  useEffect(() => {
    if (paymentCard || confirmation || loading || !cardContainerRef.current) return undefined;

    let cancelled = false;

    const initCard = async () => {
      try {
        const handle = await paymentGateway.mountCard('directory-card-container');
        if (cancelled) {
          paymentGateway.destroyCard(handle);
          return;
        }
        setPaymentCard(handle);
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            error.message || 'We could not load the secure payment form. Please refresh and try again.'
          );
        }
      }
    };

    initCard();
    return () => {
      cancelled = true;
    };
  }, [paymentCard, confirmation, loading]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleListingChange = (event) => {
    const { name, value } = event.target;
    setNewListing((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (!selectedPackage) {
        setErrorMessage('Choose a package to continue.');
        return;
      }
      if (listingMode === 'existing' && !sourceKey) {
        setErrorMessage('Choose the listing you want to promote.');
        return;
      }
      if (listingMode === 'new' && !newListing.title.trim()) {
        setErrorMessage('Enter the business or campaign name for the new advertisement.');
        return;
      }
      if (!paymentCard) {
        setErrorMessage('The payment form is still loading. Please wait a moment.');
        return;
      }

      const [sourceType, sourceId] = sourceKey.split(':');

      setIsSubmitting(true);
      setErrorMessage('');

      try {
        const token = await paymentGateway.tokenize(paymentCard);
        const payload = {
          package_id: selectedPackage.id,
          source_idempotency_key: idempotencyKey.current,
          starts_at: startsAt || undefined,
          advertiser_name: form.advertiser_name,
          advertiser_email: form.advertiser_email,
          advertiser_phone: form.advertiser_phone || undefined,
          payment_source_id: token,
          auto_renew: autoRenew,
        };

        if (listingMode === 'existing') {
          payload.source_type = sourceType;
          payload.source_id = sourceId;
        } else {
          payload.listing = {
            title: newListing.title.trim(),
            description: newListing.description.trim() || undefined,
            website_url: newListing.website_url.trim() || undefined,
            phone: newListing.phone.trim() || undefined,
            directory_category_id: newListing.directory_category_id
              ? Number(newListing.directory_category_id)
              : undefined,
            directory_location_id: newListing.directory_location_id
              ? Number(newListing.directory_location_id)
              : undefined,
            keywords: newListing.keywords
              .split(',')
              .map((keyword) => keyword.trim())
              .filter(Boolean)
              .slice(0, 12),
          };
        }

        const result = await createDirectoryCheckout(payload);

        setConfirmation(result);
        showSuccess(
          result.listingStatus === 'draft'
            ? 'Campaign booked. Your new ad is ready for creative review.'
            : 'Campaign booked successfully.',
          5000
        );
      } catch (error) {
        // A sold-out placement means this attempt can never succeed, so the next
        // try must not reuse a key the server may already have consumed.
        if (error.status === 409) {
          idempotencyKey.current = newIdempotencyKey();
          const refreshed = await fetchDirectoryInventory().catch(() => null);
          if (refreshed?.data) setInventory(refreshed.data);
        }
        setErrorMessage(error.message || 'We could not complete your campaign purchase.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [autoRenew, form, listingMode, newListing, paymentCard, selectedPackage, showSuccess, sourceKey, startsAt]
  );

  if (confirmation) {
    return (
      <main className="directory-checkout">
        <div className="directory-checkout__shell">
          <section className="directory-checkout__confirmation">
            <i className="fas fa-circle-check" aria-hidden="true" />
            <h1>Your campaign is booked.</h1>
            <p>
              We emailed the details to <strong>{form.advertiser_email}</strong>.{' '}
              {confirmation.listingStatus === 'draft'
                ? 'Your new advertisement is now in creative review; it will publish after artwork and approval are complete.'
                : `Your placement goes live on ${new Date(startsAt).toLocaleDateString()}.`}{' '}
              You can track impressions, clicks, and contacts from your campaign report.
            </p>
            <dl>
              <div>
                <dt>Campaign</dt>
                <dd>#{confirmation.campaignId}</dd>
              </div>
              <div>
                <dt>Package</dt>
                <dd>{selectedPackage?.name}</dd>
              </div>
              <div>
                <dt>Charged</dt>
                <dd>{money(selectedPackage?.price, selectedPackage?.currency)}</dd>
              </div>
              {confirmation.listingId && (
                <div>
                  <dt>Ad draft</dt>
                  <dd>#{confirmation.listingId}</dd>
                </div>
              )}
            </dl>
            <div className="directory-checkout__confirmation-actions">
              {confirmation.receiptUrl && (
                <a href={confirmation.receiptUrl} target="_blank" rel="noopener noreferrer">
                  View receipt
                </a>
              )}
              <button type="button" onClick={() => navigate('/directory')}>
                Back to the Directory
              </button>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="directory-checkout">
      <div className="directory-checkout__shell">
        <header className="directory-checkout__header">
          <p className="directory-checkout__kicker">Guhso Directory advertising</p>
          <h1>Book your placement</h1>
          <p className="directory-checkout__lede">
            Choose a package, promote an existing listing or submit a new advertisement, and select
            your campaign start date. Availability updates live.
          </p>
        </header>

        {loading && <p className="directory-checkout__status">Loading packages…</p>}
        {!loading && loadError && (
          <div className="directory-checkout__alert" role="alert">
            {loadError}
          </div>
        )}

        {!loading && !loadError && packages.length === 0 && (
          <div className="directory-checkout__alert" role="status">
            No advertising packages are on sale right now. <Link to="/directory">Browse the Directory</Link>.
          </div>
        )}

        {!loading && !loadError && packages.length > 0 && (
          <form className="directory-checkout__grid" onSubmit={handleSubmit}>
            <section className="directory-checkout__panel">
              <h2>1. Choose a package</h2>
              <div className="directory-checkout__packages" role="radiogroup" aria-label="Advertising packages">
                {packages.map((item) => {
                  const slots = inventory.find((slot) => slot.key === item.placement?.key);
                  const isSoldOut = slots?.available === 0;
                  return (
                    <label
                      key={item.id}
                      className={`directory-checkout__package${
                        String(item.id) === packageId ? ' is-selected' : ''
                      }${isSoldOut ? ' is-sold-out' : ''}`}
                    >
                      <input
                        type="radio"
                        name="package"
                        value={item.id}
                        checked={String(item.id) === packageId}
                        onChange={(event) => setPackageId(event.target.value)}
                      />
                      <span className="directory-checkout__package-body">
                        <span className="directory-checkout__package-placement">
                          {item.placement?.name}
                        </span>
                        <strong>{item.name}</strong>
                        <span className="directory-checkout__package-price">
                          {money(item.price, item.currency)}
                          <small> / {item.durationDays} days</small>
                        </span>
                        {item.features?.length > 0 && (
                          <ul>
                            {item.features.map((feature) => (
                              <li key={feature}>{feature}</li>
                            ))}
                          </ul>
                        )}
                        <span className={`directory-checkout__slots${isSoldOut ? ' is-sold-out' : ''}`}>
                          {slots === undefined || slots === null
                            ? 'Checking availability'
                            : isSoldOut
                              ? 'Sold out for these dates'
                              : `${slots.available} of ${slots.slotLimit} available`}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </section>

            <section className="directory-checkout__panel">
              <h2>2. Choose or create the advertisement</h2>
              <div className="directory-checkout__mode" role="group" aria-label="Advertisement source">
                <button
                  type="button"
                  className={listingMode === 'new' ? 'is-active' : ''}
                  onClick={() => setListingMode('new')}
                >
                  Create a new ad
                </button>
                <button
                  type="button"
                  className={listingMode === 'existing' ? 'is-active' : ''}
                  onClick={() => setListingMode('existing')}
                  disabled={listings.length === 0}
                >
                  Promote an existing listing
                </button>
              </div>

              {listingMode === 'existing' ? (
                <label className="directory-checkout__field">
                  <span>Listing</span>
                  <select value={sourceKey} onChange={(event) => setSourceKey(event.target.value)} required>
                    <option value="">Choose a listing…</option>
                    {listings.map((listing) => (
                      <option key={sourceKeyOf(listing)} value={sourceKeyOf(listing)}>
                        {listing.title}
                        {listing.source === 'product' ? ' (product)' : ''}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <div className="directory-checkout__new-listing">
                  <label className="directory-checkout__field">
                    <span>Business or campaign name</span>
                    <input name="title" value={newListing.title} onChange={handleListingChange} required />
                  </label>
                  <label className="directory-checkout__field">
                    <span>Description <small>(optional)</small></span>
                    <textarea name="description" value={newListing.description} onChange={handleListingChange} rows="4" />
                  </label>
                  <div className="directory-checkout__field-row">
                    <label className="directory-checkout__field">
                      <span>Category <small>(optional)</small></span>
                      <select name="directory_category_id" value={newListing.directory_category_id} onChange={handleListingChange}>
                        <option value="">No category yet</option>
                        {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                      </select>
                    </label>
                    <label className="directory-checkout__field">
                      <span>Location <small>(optional)</small></span>
                      <select name="directory_location_id" value={newListing.directory_location_id} onChange={handleListingChange}>
                        <option value="">Online / not specified</option>
                        {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
                      </select>
                    </label>
                  </div>
                  <label className="directory-checkout__field">
                    <span>Website <small>(optional)</small></span>
                    <input name="website_url" type="url" placeholder="https://example.com" value={newListing.website_url} onChange={handleListingChange} autoComplete="url" />
                  </label>
                  <label className="directory-checkout__field">
                    <span>Phone <small>(optional)</small></span>
                    <input name="phone" value={newListing.phone} onChange={handleListingChange} autoComplete="tel" />
                  </label>
                  <label className="directory-checkout__field">
                    <span>Search keywords <small>(optional, separated by commas)</small></span>
                    <input name="keywords" value={newListing.keywords} onChange={handleListingChange} placeholder="coffee, delivery, Kingston" />
                  </label>
                  <p className="directory-checkout__hint">Website and location are optional. After payment, the team will finish the artwork and approve the ad before it appears publicly.</p>
                </div>
              )}

              <label className="directory-checkout__field">
                <span>Start date</span>
                <input
                  type="date"
                  value={startsAt}
                  min={todayIso()}
                  onChange={(event) => setStartsAt(event.target.value)}
                  required
                />
              </label>
            </section>

            <section className="directory-checkout__panel">
              <h2>3. Your details</h2>
              <label className="directory-checkout__field">
                <span>Name</span>
                <input
                  name="advertiser_name"
                  value={form.advertiser_name}
                  onChange={handleChange}
                  autoComplete="name"
                  required
                />
              </label>
              <label className="directory-checkout__field">
                <span>Email</span>
                <input
                  name="advertiser_email"
                  type="email"
                  value={form.advertiser_email}
                  onChange={handleChange}
                  autoComplete="email"
                  required
                />
              </label>
              <label className="directory-checkout__field">
                <span>Phone <small>(optional)</small></span>
                <input
                  name="advertiser_phone"
                  value={form.advertiser_phone}
                  onChange={handleChange}
                  autoComplete="tel"
                />
              </label>
              <label className="directory-checkout__checkbox">
                <input
                  type="checkbox"
                  checked={autoRenew}
                  onChange={(event) => setAutoRenew(event.target.checked)}
                />
                <span>
                  Renew this campaign automatically each period. You can cancel any time.
                </span>
              </label>
            </section>

            <section className="directory-checkout__panel directory-checkout__panel--pay">
              <h2>4. Payment</h2>
              {selectedPackage && (
                <div className="directory-checkout__summary">
                  <div>
                    <span>{selectedPackage.name}</span>
                    <strong>{money(selectedPackage.price, selectedPackage.currency)}</strong>
                  </div>
                  <p>
                    {selectedPackage.durationDays}-day campaign
                    {autoRenew ? ', renewing automatically' : ', one-time purchase'}.
                  </p>
                </div>
              )}

              <div id="directory-card-container" className="directory-checkout__card" ref={cardContainerRef} />

              {errorMessage && (
                <div className="directory-checkout__alert" role="alert">
                  {errorMessage}
                </div>
              )}

              <button
                type="submit"
                className="directory-checkout__submit"
                disabled={isSubmitting || !paymentCard || soldOut}
              >
                {isSubmitting
                  ? 'Booking your campaign…'
                  : soldOut
                    ? 'Sold out for these dates'
                    : listingMode === 'new'
                      ? `Pay ${money(selectedPackage?.price, selectedPackage?.currency)} and submit ad`
                      : `Pay ${money(selectedPackage?.price, selectedPackage?.currency)} and go live`}
              </button>
              <p className="directory-checkout__fineprint">
                Your card is charged the package price shown above. Placements are labelled as paid
                across the Directory.
              </p>
            </section>
          </form>
        )}
      </div>
    </main>
  );
}
